import { Component, model, input, inject, computed, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { EditorModule } from 'primeng/editor';
import { EditorSpellcheckDirective } from '../../../../shared/directives/editor-spellcheck.directive';
import { LineItemModel } from '../../../../models/template.model';
import { SpeechService } from '../../../../services/speech.service';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { ValidationService } from '../../../../services/validation.service';

@Component({
  selector: 'app-richtext-control',
  standalone: true,
  imports: [CommonModule, FormsModule, EditorModule, EditorSpellcheckDirective],
  template: `
    <div class="checklist-control-row checklist-control-row--top">
      <label [for]="'richtext-' + lineItem().lineItemId" class="checklist-control-label">
        {{ lineItem().name }}@if (lineItem().isRequired) { <span class="required-indicator">*</span>}
      </label>
      <div class="checklist-control-input richtext-editor-container">
        @if (disabled()) {
          <div class="richtext-readonly ql-snow">
            <div class="ql-editor" [innerHTML]="sanitizedReadonlyHtml"></div>
          </div>
        } @else {
          <p-editor
            [id]="'richtext-' + lineItem().lineItemId"
            [(ngModel)]="value"
            [readonly]="false"
            [style]="{ height: '200px' }"
            [ngClass]="{'ng-invalid ng-dirty': hasError()}"
            placeholder="Enter formatted text..."
            (onBlur)="onBlur()">
            <ng-template #header>
              <span class="ql-formats">
                <button type="button" class="ql-bold" aria-label="Bold"></button>
                <button type="button" class="ql-italic" aria-label="Italic"></button>
                <button type="button" class="ql-underline" aria-label="Underline"></button>
                <button type="button" class="ql-strike" aria-label="Strikethrough"></button>
              </span>
              <span class="ql-formats">
                <select class="ql-color" aria-label="Text Color"></select>
                <select class="ql-background" aria-label="Background Color"></select>
              </span>
              <span class="ql-formats">
                <button type="button" class="ql-list" value="ordered" aria-label="Ordered List"></button>
                <button type="button" class="ql-list" value="bullet" aria-label="Bullet List"></button>
              </span>
              <span class="ql-formats">
                <select class="ql-align" aria-label="Text Alignment"></select>
              </span>
              <span class="ql-formats">
                <button type="button" class="ql-link" aria-label="Insert Link"></button>
              </span>
              <span class="ql-formats">
                <button type="button" class="ql-clean" aria-label="Clear Formatting"></button>
              </span>
            </ng-template>
          </p-editor>
        }
        @if (hasError()) {
          @for (err of errors(); track $index) {
            <small class="p-error d-block">{{ err }}</small>
          }
        }
      </div>
      <div class="ctrl-speaker-col">
        <button type="button" class="ctrl-speaker-btn" (click)="speakLabel()"
          [attr.aria-label]="'Read label: ' + lineItem().name">
          <i class="ri-volume-up-line"></i>
        </button>
      </div>
    </div>
  `,
  styles: [`
    .richtext-editor-container {
      width: 100%;
    }
    :host ::ng-deep .p-editor-container {
      width: 100%;
    }
    :host ::ng-deep .p-editor-container .p-editor-toolbar {
      background: var(--surface-100);
      border-top-left-radius: 6px;
      border-top-right-radius: 6px;
    }
    :host ::ng-deep .p-editor-container .p-editor-content {
      border-bottom-left-radius: 6px;
      border-bottom-right-radius: 6px;
    }
    :host ::ng-deep .p-editor-container .p-editor-content .ql-editor {
      min-height: 150px;
    }

    .richtext-readonly {
      border: 1px solid var(--color-border-default);
      border-radius: 6px;
      background: var(--color-bg-surface);
      min-height: 150px;
    }

    .richtext-readonly :deep(.ql-editor) {
      min-height: 150px;
      height: auto !important;
      overflow-y: visible !important;
    }

    .richtext-readonly :deep(.ql-editor ul),
    .richtext-readonly :deep(.ql-editor ol) {
      margin: 0.5rem 0;
      padding-left: 1.5rem;
    }

    .richtext-readonly :deep(.ql-editor ul > li) {
      list-style-type: disc;
      list-style-position: outside;
      padding-left: 0;
    }

    .richtext-readonly :deep(.ql-editor ol > li) {
      list-style-type: decimal;
      list-style-position: outside;
      padding-left: 0;
    }
  `]
})
export class RichtextControlComponent {
  lineItem = input.required<LineItemModel>();
  value = model<string>('');
  disabled = input<boolean>(false);
  showError = input<boolean>(false);

  private speechService = inject(SpeechService);
  private sanitizer = inject(DomSanitizer);
  private validationService = inject(ValidationService);
  touched = signal(false);

  errors = computed(() => {
    if (!this.showError() && !this.touched()) return [];
    return this.validationService.validate(this.lineItem(), this.value());
  });

  hasError = computed(() => this.errors().length > 0);

  onBlur(): void {
    this.touched.set(true);
  }

  speakLabel(): void {
    this.speechService.speak(this.lineItem().name);
  }

  get sanitizedReadonlyHtml(): SafeHtml {
    const html = (this.value() || '').replace(/&nbsp;/g, ' ');
    const normalizedHtml = this.normalizeQuillLists(html);
    return this.sanitizer.bypassSecurityTrustHtml(normalizedHtml);
  }

  private normalizeQuillLists(html: string): string {
    if (!html || typeof DOMParser === 'undefined') return html;

    const parser = new DOMParser();
    const doc = parser.parseFromString(`<div>${html}</div>`, 'text/html');
    const root = doc.body.firstElementChild as HTMLDivElement | null;
    if (!root) return html;

    const orderedLists = Array.from(root.querySelectorAll('ol'));
    for (const ol of orderedLists) {
      const liItems = Array.from(ol.children).filter((node) => node.tagName === 'LI') as HTMLLIElement[];
      if (liItems.length === 0 || !liItems.some((li) => li.hasAttribute('data-list'))) {
        continue;
      }

      const replacement = doc.createDocumentFragment();
      let currentList: HTMLOListElement | HTMLUListElement | null = null;
      let currentListTag: 'ol' | 'ul' | null = null;

      for (const li of liItems) {
        const listMode = li.getAttribute('data-list');
        const targetTag: 'ol' | 'ul' = listMode === 'bullet' ? 'ul' : 'ol';

        if (!currentList || currentListTag !== targetTag) {
          currentList = doc.createElement(targetTag);
          replacement.appendChild(currentList);
          currentListTag = targetTag;
        }

        const newLi = doc.createElement('li');
        for (const attr of Array.from(li.attributes)) {
          if (attr.name !== 'data-list') {
            newLi.setAttribute(attr.name, attr.value);
          }
        }
        while (li.firstChild) {
          newLi.appendChild(li.firstChild);
        }
        currentList.appendChild(newLi);
      }

      ol.replaceWith(replacement);
    }

    return root.innerHTML;
  }
}
